import { Div, Image, P } from '@kizi/core-components';
import React from 'react';
import {
  Price,
  ProductComparisonLabel,
  ProsCons,
  ProsConsItem,
  sendCustomEvent,
  Sustainability,
} from '../../../components';
import { ComparisonSubmitButton } from '../../../components/comparison/ComparisonSubmitButton';
import { formatPrice } from '../../global';
import { carCoveragesMapping } from '../../hardcodedDataTransformers';
import { CarProduct, Coverage } from '../../types';
import { handleProsNCons } from '../energy/helpers';

const comparisonOnApplyFn = (wizard, product: CarProduct) => {
  if (product.provider.attributes?.affiliateUrl) {
    const link = product.links.find(({ action }) => action === 'order');
    if (link && link.url) {
      sendCustomEvent('affiliate-apply', {
        selectedProduct: product,
      });
      const url = new URL(window.location.href);
      if (url.searchParams.has('affiliateUrl')) {
        return;
      }
      url.searchParams.set('step', 'overview');
      url.searchParams.append('affiliateUrl', link.url);
      window.open(url.toString(), '_blank');
      return;
    }
  }
  wizard?.send([
    {
      type: 'SET_FORM_FIELDS',
      name: 'overview',
      fields: [
        { name: 'selectedProduct', value: product as any },
        { name: 'totalPriceMonth', value: product?.price.month.total },
        { name: 'totalPriceYear', value: product?.price.year.total },
        { name: 'providerName', value: product?.provider.name },
      ],
    },
    {
      type: 'GOTO',
      index: 5,
    },
  ]);
};

export const getProductComparisonOverviewCar = (
  data: CarProduct,
  _labels,
  priceMode: 'month' | 'year',
) => {
  const { price, filters, provider, featured, rating, availability } = data;
  const { logo, name, attributes } = provider;
  const { mainCoverages = [], additionalCoverages = [] } = price;
  const discount = price[priceMode].discountIncluded;
  const priceLabel = priceMode === 'month' ? 'maand' : 'jaar';
  const totalPrice = price[priceMode].total;
  const disabled = !availability.acceptance.accepted;
  const generateCoverageItem = (coverages: Coverage[]) =>
    coverages.map((coverage, index) => (
      <P variant="footnoteRegular" key={index}>
        {carCoveragesMapping[coverage.nameShort]}
      </P>
    ));
  const generateFeatureItem = (featureName, tooltip, extraFeature = false) => {
    if (extraFeature && attributes?.extraInfo?.length) {
      return (
        <ProsConsItem
          text={attributes.extraInfo[0].title}
          type="warning"
          tooltip={attributes.extraInfo[0].content}
          childVariant="featureItemComparison"
        />
      );
    }
    if (filters[featureName] && filters[featureName] === 'Ja') {
      return (
        <ProsConsItem
          text={featureName}
          type="pros"
          tooltip={tooltip}
          childVariant="featureItemComparison"
        />
      );
    }
    return null;
  };
  return [
    {
      content: (
        <Div display="flex" flexDirection="column" alignItems="center">
          <Div display="flex" variant="productLogoWrapper" mb={[2, null, 5]}>
            {logo ? (
              <Image
                alt={`logo-${name}`}
                height="100%"
                src={logo}
                width="100%"
              />
            ) : (
              <Div height={40} />
            )}
          </Div>
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: <Div>{generateCoverageItem(mainCoverages)}</Div>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: additionalCoverages.length && (
        <Div>{generateCoverageItem(additionalCoverages)}</Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <Div>
          {generateFeatureItem(
            'Dagelijks opzegbaar',
            'De meeste autoverzekeringen kun je na een jaar opzeggen. Vind je dat te lang? Kies dan voor een verzekering die direct opzegbaar is.',
          )}
          {generateFeatureItem(
            'Aanschafwaarderegeling',
            'Rijd je je auto total loss of wordt je auto gestolen? Dan kun je de aanschafwaarde van je auto vergoed krijgen.',
          )}
          {generateFeatureItem('', '', true)}
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <P variant="footnoteRegular">
          {formatPrice(featured['Eigen risico'].value)}
        </P>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <Sustainability
          score={String(rating.consumentenbondTestScore)}
          variant="sustainabilityRegular"
          iconName="favicon"
        />
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <Price
          discount={discount}
          label={`per ${priceLabel}`}
          value={totalPrice}
          variant={disabled ? 'priceBigDisabled' : 'priceBig'}
        />
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: availability.acceptance.accepted ? (
        <Div display="flex" justifyContent="center">
          <ComparisonSubmitButton
            data={data}
            fieldName="overview.selectedProduct"
            nextIndex={5}
            onSubmitFn={comparisonOnApplyFn}
            isProductEnabled={availability.acceptance.accepted}
          />
        </Div>
      ) : (
        <Div display="flex" justifyContent="center">
          <Div>Geen samenwerking met verzekeraar</Div>
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const getProductComparisonAbout = (data: CarProduct) => {
  const { provider } = data;
  const { description, attributes } = provider;
  const { prosAndCons, features } = attributes;
  const allowed = ['title1', 'title2'];
  const pros = handleProsNCons(prosAndCons?.pros, allowed);
  const cons = handleProsNCons(prosAndCons?.cons, allowed);

  const handleFeature = (featureName) => {
    if (!features || !features[featureName]) {
      return null;
    }

    return <P variant="footnoteRegular">{features[featureName]}</P>;
  };

  return [
    {
      content: <P variant="footnoteRegular">{description}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: prosAndCons && (
        <Div display="flex" flexDirection="column">
          {pros && <ProsCons data={pros} onlyPros />}
          {cons && <ProsCons data={cons} onlyCons />}
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: handleFeature('Telefonisch contact mogelijk'),
      isVisible: true,
      toggleable: false,
    },
    {
      content: handleFeature('Type polis'),
      isVisible: true,
      toggleable: false,
    },
    {
      content: handleFeature('Eenmalige poliskosten'),
      isVisible: true,
      toggleable: false,
    },
    {
      content: handleFeature('Bijzonderheden'),
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const getProductComparisonPolicy = (data: CarProduct) => {
  const { featured, features, filters } = data;
  const featuresMapping = {
    'Dagelijks opzegbaar': 'freeToCancel',
    Aanschafwaarderegeling: 'purchaseValueArrangement',
    Nieuwwaarderegeling: 'newValueArrangement',
    'Gelijkwaardige autoregeling': 'similarCarArrangement',
  };

  const generateFeatures = (featureKey, key) => {
    const featureValue = featuresMapping[featureKey];
    if (!features[featureValue]) {
      return null;
    }
    const type = features[featureValue] === 'Ja' ? 'pros' : 'danger';
    const tooltip = features[`${featureValue}SubText`];
    return (
      <ProsConsItem
        key={key}
        text={featureKey}
        type={type}
        tooltip={tooltip}
        childVariant="featureItemComparison"
      />
    );
  };

  return [
    {
      content: (
        <P variant="footnoteRegular">
          {formatPrice(featured['Eigen risico'].value)}
        </P>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <Div display="flex" flexDirection="column">
          {Object.keys(featuresMapping).map((feature, index) =>
            generateFeatures(feature, index),
          )}
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: <P variant="footnoteRegular">{filters['Vrije keuze garage']}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <P variant="footnoteRegular">
          {filters['Eigen risico bij niet aangesloten garage']}
        </P>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <P variant="footnoteRegular">
          {filters['Maximale vergoeding bij accessoires']}
        </P>
      ),
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const getProductComparisonPrices = (
  data: CarProduct,
  labels: ProductComparisonLabel[],
  priceMode: 'month' | 'year',
) => {
  const { price, provider } = data;
  const additionalCoverageLabels = labels.filter(
    (label) => label.from === 'additionalCoverage',
  );
  const mainCoverageLabels = labels.filter(
    (label) => label.from === 'mainCoverage',
  );
  const filterLabels = (labelsArray, priceQuery) => {
    return labelsArray.map(({ label }) => {
      const coverageWithValue = price[priceQuery]?.find(
        (cover) => carCoveragesMapping[cover.nameShort] === label,
      );
      return {
        content: (
          <P variant="footnoteRegular">
            {coverageWithValue
              ? formatPrice(coverageWithValue.premium[priceMode])
              : 'Niet inbegrepen'}
          </P>
        ),
        isVisible: true,
        toggleable: false,
      };
    });
  };
  return [
    ...filterLabels(mainCoverageLabels, 'mainCoverages'),
    ...filterLabels(additionalCoverageLabels, 'additionalCoverages'),
    {
      content: (
        <P variant="footnoteRegular">
          {formatPrice(price[priceMode].administrationCosts || 0)}
        </P>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <P variant="footnoteRegular">{formatPrice(price[priceMode].total)}</P>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <P variant="footnoteRegular">
          {formatPrice(
            provider.attributes?.features?.['Eenmalige poliskosten'] || 0,
          )}
        </P>
      ),
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const getProductComparisonPolicyExtended = (
  data: CarProduct,
  _labels,
  _priceMode,
  _additionalData,
  products: CarProduct[],
) => {
  if (data?.policyFeatures) {
    const filteredProducts = products.filter(
      (product) => !Array.isArray(product?.policyFeatures),
    );

    return Array.isArray(data.policyFeatures) && filteredProducts.length > 0
      ? [
          ...Object.keys(filteredProducts[0]?.policyFeatures).map((key) => {
            const currentFeature = filteredProducts[0]?.policyFeatures[key];
            return {
              label: 'Gegevens worden niet verstrekt',
              children: currentFeature.features.map(() => '-'),
              childrenProps: currentFeature.features.map(() => ({
                textAlign: 'center',
              })),
            };
          }),
        ]
      : [
          ...Object.keys(data?.policyFeatures).map((key) => {
            const currentFeature = data?.policyFeatures[key];

            return {
              label: currentFeature.name,
              children: currentFeature.features.map((feature) => feature.value),
              childrenProps: currentFeature.features.map((feature, index) => ({
                backgroundColor: filteredProducts.every(
                  (_product) =>
                    _product.policyFeatures?.[key].features[index].value ===
                    feature.value,
                )
                  ? 'initial'
                  : '#FFA61733',
              })),
            };
          }),
        ];
  } else {
    return [
      {
        content: <P>Bezig met laden...</P>,
        isVisible: true,
        toggleable: false,
        label: '',
        children: [''],
      },
    ];
  }
};

export const carHelpers = {
  overview: getProductComparisonOverviewCar,
  about: getProductComparisonAbout,
  policy: getProductComparisonPolicy,
  prices: getProductComparisonPrices,
  policyExtended: getProductComparisonPolicyExtended,
};
